import React, { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import PortalGate from '../../components/portal/PortalGate';

const PortalLayout = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [clientData, setClientData] = useState(null);
    const [errorType, setErrorType] = useState(null); // 'invalid' | 'expired' | null

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setErrorType('invalid');
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(`/api/get-status?token=${token}`);
                if (res.status === 404) {
                    setErrorType('invalid');
                } else if (res.status === 403) {
                    setErrorType('expired');
                } else if (!res.ok) {
                    setErrorType('invalid');
                } else {
                    const data = await res.json();
                    if (data.error) {
                        setErrorType('invalid');
                    } else {
                        setClientData(data);
                    }
                }
            } catch {
                setErrorType('invalid');
            }
            setLoading(false);
        };

        validateToken();
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0A14] flex items-center justify-center text-[#7B61FF]">
                <Loader2 className="w-8 h-8 animate-spin" />
            </div>
        );
    }

    if (errorType === 'expired') {
        return (
            <div className="min-h-screen bg-[#0A0A14] flex flex-col items-center justify-center text-center p-6">
                <div className="font-bold text-xl tracking-tight leading-none mb-8">
                    Roof<span className="text-[#7B61FF]">Claw</span>{' '}
                    <span className="text-[#F0EFF4]/40 text-sm font-data">PORTAL</span>
                </div>
                <div className="w-16 h-16 rounded-full bg-[#7B61FF]/10 flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-[#7B61FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h1 className="text-2xl font-heading font-bold text-[#F0EFF4] mb-3">Link Expired</h1>
                <p className="text-[#F0EFF4]/60 max-w-sm mb-8 text-sm leading-relaxed">
                    This portal link has expired for your security. Please contact your project manager for a new link.
                </p>
                <a
                    href="mailto:vito@roofclaw.com?subject=Portal%20Link%20Expired"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#7B61FF] hover:bg-[#6B51EF] text-[#F0EFF4] font-bold py-2.5 px-6 rounded-xl transition-colors text-sm"
                >
                    Contact Support
                </a>
            </div>
        );
    }

    if (errorType === 'invalid') {
        return (
            <div className="min-h-screen bg-[#0A0A14] flex flex-col items-center justify-center text-center p-6">
                <div className="font-bold text-xl tracking-tight leading-none mb-8">
                    Roof<span className="text-[#7B61FF]">Claw</span>{' '}
                    <span className="text-[#F0EFF4]/40 text-sm font-data">PORTAL</span>
                </div>
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                </div>
                <h1 className="text-2xl font-heading font-bold text-[#F0EFF4] mb-3">Link Not Found</h1>
                <p className="text-[#F0EFF4]/60 max-w-sm mb-8 text-sm leading-relaxed">
                    This portal link doesn't exist. Please check the URL or contact your project manager for the correct link.
                </p>
                <a
                    href="mailto:vito@roofclaw.com?subject=Portal%20Link%20Not%20Working"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#7B61FF] hover:bg-[#6B51EF] text-[#F0EFF4] font-bold py-2.5 px-6 rounded-xl transition-colors text-sm"
                >
                    Contact Support
                </a>
            </div>
        );
    }

    const firstName = (clientData?.name || clientData?.client_name || '').split(' ')[0];
    const company = clientData?.company || null;

    return (
        <PortalGate token={token}>
        <div className="min-h-screen bg-[#0A0A14] text-[#F0EFF4] selection:bg-[#7B61FF] selection:text-[#F0EFF4] font-heading flex flex-col">
            {/* Minimal Portal Header */}
            <header className="border-b border-[#F0EFF4]/10 bg-[#0A0A14] sticky top-0 z-50">
                <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
                    {/* Left: back nav */}
                    <a
                        href="https://www.roofclaw.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#F0EFF4]/40 hover:text-[#F0EFF4]/80 text-sm transition-colors flex items-center gap-1 shrink-0"
                    >
                        ← roofclaw.com
                    </a>

                    {/* Center: logo + client context */}
                    <div className="flex flex-col items-center text-center">
                        <div className="font-bold text-base tracking-tight leading-none">
                            Roof<span className="text-[#7B61FF]">Claw</span>{' '}
                            <span className="text-[#F0EFF4]/40 text-xs font-data">PORTAL</span>
                        </div>
                        {(firstName || company) && (
                            <span className="text-[#F0EFF4]/40 text-xs mt-0.5">
                                {[firstName, company].filter(Boolean).join(' · ')}
                            </span>
                        )}
                    </div>

                    {/* Right: help link */}
                    <a
                        href={`mailto:vito@roofclaw.com?subject=${encodeURIComponent(`Portal Question — ${[firstName, company].filter(Boolean).join(' / ') || 'Client'}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-bold px-3 py-1.5 rounded-full border border-[#7B61FF]/50 text-[#7B61FF] hover:bg-[#7B61FF] hover:text-[#F0EFF4] transition-colors shrink-0"
                    >
                        Need Help?
                    </a>
                </div>
            </header>

            {/* Portal Content Area */}
            <main className="flex-grow max-w-3xl mx-auto w-full px-6 py-12">
                {/* We pass clientData via context to nested routes */}
                <Outlet context={{ clientData, token }} />
            </main>
        </div>
        </PortalGate>
    );
};

export default PortalLayout;
